import type { IServiceTaskChecklist } from '../../admin-panel/task.interface';
import { IAvailability } from '../../admin-panel/availability.interface';

export interface IServiceStep {
  duration: number;  // in minutes
  // False marks a waiting step (colour developing, keratin setting): the visit still spans
  // it, but the provider is free, so another appointment may be booked inside it. At least
  // one step must be true — an all-waiting service would occupy nobody and could be booked
  // on top of anything.
  requiresProvider: boolean;
  description?: string;
}

/**
 * Per-employee override of a service's price/duration. Only fields that DIFFER from the
 * service defaults need to be set. Resolve with resolveServiceForProvider() /
 * resolveServiceDuration() / resolveServicePrice() from @torim-app/utils.
 */
export interface IServiceProviderOverride {
  providerId: string;
  price?: number;
  maxPrice?: number;
  duration?: number; // in minutes
}

export enum ServiceType {
  REGULAR = 'regular',
  COMPLEMENTARY = 'complementary',
  HIDDEN = 'hidden'
}

export enum PriceTypeEnum {
  FIXED = 'fixed',
  RANGE = 'range',
  FROM = 'from',
}

/**
 * A stock-photo suggestion for a service image, returned by the suggested-images picker.
 * `previewUrl` is shown in the gallery; `sourceUrl` is the (possibly higher-res) URL the
 * backend downloads and re-uploads to ImageKit when the owner picks it.
 */
export interface ISuggestedImage {
  id: string;
  previewUrl: string;
  sourceUrl: string;
}

export interface IService {
  id: string;
  imageUrl: string;
  name: string;
  description?: string;
  // Free-text note shown to the customer at checkout, below this service line item.
  notes?: string;
  price: number;
  maxPrice?: number;
  // Optional sale price. When set, this is the EFFECTIVE price charged/shown (use
  // effectiveServicePrice()). An appointment snapshots the effective price at booking
  // (IAppointment.bookedServicePrices) so removing the discount later doesn't change
  // the price of already-booked appointments.
  discountedPrice?: number;
  // 'fixed' uses price. 'range' uses price..maxPrice. 'from' uses price as starting amount; cannot be paid online.
  priceType?: PriceTypeEnum;
  // Hex color used to tint admin calendar events and (later) other surfaces. Optional - falls back to default.
  color?: string;
  // Duration in minutes. `null` on a stepped service (see `steps`), whose length is the
  // sum of its steps instead. Always read it through resolveServiceDuration() from
  // @torim-app/utils rather than directly, so both shapes are handled.
  duration: number | null;
  // Per-employee price/duration overrides. Empty/absent = every provider uses the defaults.
  providerOverrides?: Array<IServiceProviderOverride>;
  visible: boolean;
  providingClient: string | string[];
  selectedClientsOnly?: boolean;
  steps?: Array<IServiceStep>;
  serviceType: ServiceType;
  attachedToServices?: Array<string>; // Service IDs this complementary service applies to
  category?: string;

  // When true, this service can only be booked within the per-weekday windows in
  // `availabilityHours`, intersected with the provider's working hours. When false
  // or undefined, the service follows the provider's working hours with no extra limit.
  limitedHours?: boolean;
  // Per-weekday allowed booking windows for this service (Sun–Sat → list of {from,to}).
  // Only applied when `limitedHours` is true. An empty day list means the service is
  // not bookable on that weekday.
  availabilityHours?: IAvailability;

  // When true, booking this service doesn't require the advance/schedule payment.
  // A multi-service booking requires payment if ANY selected service is non-exempt,
  // and the advance percentage applies to the non-exempt subtotal only.
  exemptFromAdvancePayment?: boolean;

  // Tasks copied onto every appointment of this service (prep before, care after).
  taskChecklist?: IServiceTaskChecklist;

  providingClients: string[];
}



